// Engagement tracking, added 2026-09-11 per Martina.
//
// Pushes two event families to window.dataLayer: scroll_depth and
// engaged_time. Lands in the same dataLayer the GTM snippet in theme.liquid
// already initializes (container GTM-MMMX9XMV, installed 2026-09-08 for the
// hero_cta_click/waitlist_submit A/B test events) -- this file doesn't
// hardcode that container ID, it just pushes to window.dataLayer, so it has
// no dependency on GTM's own snippet
// beyond dataLayer existing first (theme.liquid loads GTM in <head>, this
// script at the end of <body>, so ordering is already correct).
//
// New GA4 events/triggers still need to be built in GTM itself for these
// pushes to actually reach GA4 -- pushing to dataLayer alone doesn't create
// a GA4 event on its own, same as it didn't for hero_cta_click/
// waitlist_submit before someone wired those up in the container.
//
// Also mirrors every event through window.Shopify.analytics.publish(), the
// documented API for custom Shopify Web Pixel events -- a "free" bonus for
// if a custom Web Pixel extension is ever built to forward these into
// Shopify's native GA4/Meta channels directly. Wrapped in try/catch and
// optional-chained because it's untested in this environment (no live
// storefront access here -- same limitation as every other handoff in this
// project) and safe to no-op if the runtime doesn't expose it.
//
// Reuses this theme's existing >=990px / .page-wrapper scroller convention
// (see scroll-smooth.js, main.js's initHashScroll/initScrollWordReveal) --
// Horizon hands real scrolling from window to .page-wrapper at that
// breakpoint, and every scroll-reading script in this theme has to check
// both or it silently goes blind on desktop, same bug class documented
// repeatedly in this project's handoffs. Not re-discovered here, reused.

window.dataLayer = window.dataLayer || [];

function pushEvent(name, params) {
  const payload = params || {};
  window.dataLayer.push(Object.assign({ event: name }, payload));
  if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === "function") {
    try {
      window.Shopify.analytics.publish(name, payload);
    } catch (err) {
      // No subscriber registered for this custom event name -- safe to ignore.
    }
  }
}

function getScrollMetrics() {
  const isDesktopScroller = window.matchMedia("(min-width: 990px)").matches;
  const pageWrapper = document.querySelector(".page-wrapper");

  if (isDesktopScroller && pageWrapper) {
    return {
      scrollTop: pageWrapper.scrollTop,
      scrollHeight: pageWrapper.scrollHeight,
      clientHeight: pageWrapper.clientHeight,
    };
  }

  return {
    scrollTop: window.scrollY || document.documentElement.scrollTop,
    scrollHeight: document.documentElement.scrollHeight,
    clientHeight: window.innerHeight,
  };
}

function initScrollDepth() {
  const milestones = [25, 50, 75, 90, 100];
  const fired = new Set();

  function check() {
    const { scrollTop, scrollHeight, clientHeight } = getScrollMetrics();
    if (scrollHeight <= clientHeight) return; // page/section doesn't scroll -- nothing to measure

    const percent = Math.min(100, Math.round(((scrollTop + clientHeight) / scrollHeight) * 100));

    milestones.forEach((milestone) => {
      if (percent >= milestone && !fired.has(milestone)) {
        fired.add(milestone);
        pushEvent("scroll_depth", { percent_scrolled: milestone });
      }
    });
  }

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      check();
      ticking = false;
    });
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  const pageWrapper = document.querySelector(".page-wrapper");
  if (pageWrapper) pageWrapper.addEventListener("scroll", onScroll, { passive: true });

  check(); // covers loading already scrolled, e.g. a #early-access hash link
}

function initEngagementTime() {
  const milestones = [10, 30, 60, 120, 300];
  const fired = new Set();
  const IDLE_TIMEOUT_MS = 60000;

  let engagedSeconds = 0;
  let lastInteraction = Date.now();

  function markInteraction(evt) {
    if (evt && evt.isTrusted === false) return; // ignore synthetic/programmatic dispatches
    lastInteraction = Date.now();
  }

  ["mousemove", "keydown", "wheel", "touchstart", "click", "scroll"].forEach((type) => {
    window.addEventListener(type, markInteraction, { passive: true });
  });

  setInterval(() => {
    const isIdle = Date.now() - lastInteraction > IDLE_TIMEOUT_MS;
    if (document.visibilityState !== "visible" || isIdle) return;

    engagedSeconds += 1;
    milestones.forEach((milestone) => {
      if (engagedSeconds >= milestone && !fired.has(milestone)) {
        fired.add(milestone);
        pushEvent("engaged_time", { seconds: milestone });
      }
    });
  }, 1000);
}

initScrollDepth();
initEngagementTime();
